import {
  APIFieldType,
  DictionaryItem,
  DictionaryLookupTransformation,
} from '@ardoq/api-types';
import { SecondaryButton, GhostButton } from '@ardoq/button';
import { s8 } from '@ardoq/design-tokens';
import {
  Field,
  Label,
  NumberInput,
  TextInput,
  useOptimistic,
} from '@ardoq/forms';
import { Icon, IconName } from '@ardoq/icons';
import { FieldType } from '@ardoq/renderers';
import { Select, SelectOption } from '@ardoq/select';
import React from 'react';
import styled from 'styled-components';
import { DEFAULT_ARDOQ_I18NLOCALE, I18nLocale, t } from '@ardoq/i18n';
import { EditorSectionProps } from './types';
import { AutocompleteFieldSuggestion, InputConfig } from './types';
import { getSelectedDictionaryLookupFieldSelectedOption } from './utils';

type DictionaryLookupDetailsSectionProps =
  EditorSectionProps<DictionaryLookupTransformation> & {
    i18nLocale?: I18nLocale;
  };

export const DictionaryLookupDetailsSection = ({
  type,
  value: transformation,
  onValueChange: onTransformationChange,
  reactiveFieldOptions,
  options,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: DictionaryLookupDetailsSectionProps) => {
  const selectedOption = getSelectedDictionaryLookupFieldSelectedOption(
    transformation.config.additionalFields[0],
    reactiveFieldOptions
  );

  const dictionary = getDictionaryItems(
    transformation,
    createEmptyDictionaryItem(type)
  );

  const setDictionary = (dictionary: DictionaryItem[]) =>
    onTransformationChange({
      ...transformation,
      config: { ...transformation.config, dictionary },
    });

  const handleFieldChange = (field: AutocompleteFieldSuggestion | null) =>
    onTransformationChange({
      ...transformation,
      config: {
        ...transformation.config,
        additionalFields: field ? [field.name] : [],
        dictionary: [],
      },
    });

  const handleChangeItem =
    (itemIndex: number) => (entry: Partial<DictionaryItem>) =>
      setDictionary(
        dictionary.map((item, index) =>
          index === itemIndex ? { ...item, ...entry } : item
        )
      );

  const handleRemoveItem = (itemIndex: number) => () =>
    setDictionary(dictionary.filter((_item, index) => index !== itemIndex));

  const handleAddNewItem = () => {
    setDictionary([...dictionary, createEmptyDictionaryItem(type)]);
  };

  return (
    <>
      <Field
        label={t(
          i18nLocale,
          'scopeData:calculatedFields.dictionaryLookup.fieldLabel',
          'Field'
        )}
      >
        <Select
          placeholder={t(
            i18nLocale,
            'scopeData:calculatedFields.dictionaryLookup.fieldPlaceholder',
            'Select field'
          )}
          options={reactiveFieldOptions}
          value={selectedOption}
          onValueChange={handleFieldChange}
          i18nLocale={i18nLocale}
        />
      </Field>
      {selectedOption && (
        <LookupDictionary>
          <Label>
            {t(
              i18nLocale,
              'scopeData:calculatedFields.dictionaryLookup.mapFrom',
              'Map from'
            )}
          </Label>
          <Label>
            {t(
              i18nLocale,
              'scopeData:calculatedFields.dictionaryLookup.mapTo',
              'Map to'
            )}
          </Label>

          {dictionary.map((entry, index) => {
            return (
              <React.Fragment key={index}>
                <From>
                  <DictionaryInput
                    inputConfig={selectedOption.value}
                    value={entry.key}
                    onValueChange={key =>
                      handleChangeItem(index)({ key: key ?? undefined })
                    }
                    i18nLocale={i18nLocale}
                  />
                </From>
                <To>
                  {type === FieldType.TRANSFORM_LIST ? (
                    <DictionaryInput
                      inputConfig={{ type, options: options ?? [] }}
                      value={entry.value}
                      onValueChange={value =>
                        handleChangeItem(index)({ value: value ?? undefined })
                      }
                      i18nLocale={i18nLocale}
                    />
                  ) : (
                    <DictionaryInput
                      inputConfig={{ type }}
                      value={entry.value}
                      onValueChange={value =>
                        handleChangeItem(index)({ value: value ?? undefined })
                      }
                      i18nLocale={i18nLocale}
                    />
                  )}
                </To>
                <GhostButton
                  aria-label={t(
                    i18nLocale,
                    'scopeData:calculatedFields.dictionaryLookup.removeRow',
                    'Remove row'
                  )}
                  isDisabled={dictionary.length === 1}
                  onClick={handleRemoveItem(index)}
                >
                  <Icon iconName={IconName.REMOVE} />
                </GhostButton>
              </React.Fragment>
            );
          })}
          <AddNewButton onClick={handleAddNewItem}>
            <Icon iconName={IconName.ADD} />{' '}
            {t(
              i18nLocale,
              'scopeData:calculatedFields.dictionaryLookup.addRow',
              'Add row'
            )}
          </AddNewButton>
        </LookupDictionary>
      )}
    </>
  );
};

const DictionaryInput = ({
  inputConfig,
  value: externalValue,
  onValueChange: onExternalValueChange,
  i18nLocale,
}: {
  inputConfig: InputConfig;
  value: string | number | null;
  onValueChange: (value: string | number | null) => void;
  i18nLocale: I18nLocale;
}) => {
  const [value, onValueChange] = useOptimistic(
    externalValue,
    onExternalValueChange
  );

  if (
    inputConfig.type === APIFieldType.NUMBER ||
    inputConfig.type === FieldType.TRANSFORM_NUMBER
  ) {
    return (
      <NumberInput
        value={toNumberInputValue(value)}
        onValueChange={onValueChange}
      />
    );
  }

  if (
    inputConfig.type === APIFieldType.LIST ||
    inputConfig.type === FieldType.TRANSFORM_LIST
  ) {
    return (
      <Select
        placeholder={t(
          i18nLocale,
          'scopeData:calculatedFields.dictionaryLookup.selectOption',
          'Select option'
        )}
        options={inputConfig.options.map(option =>
          typeof option === 'string'
            ? { label: option, value: option }
            : (option as SelectOption<string>)
        )}
        value={value}
        onValueChange={onValueChange}
        i18nLocale={i18nLocale}
      />
    );
  }

  return <TextInput value={value ?? undefined} onValueChange={onValueChange} />;
};

const toNumberInputValue = (
  value: string | number | null
): number | undefined => {
  if (!value || value === '') {
    return undefined;
  }

  const number = Number(value);

  return Number.isNaN(number) ? undefined : number;
};

const getDictionaryItems = (
  transformation: DictionaryLookupTransformation,
  defaultItem: DictionaryItem
): DictionaryItem[] => {
  if (
    !transformation.config.dictionary ||
    transformation.config.dictionary.length === 0
  ) {
    return [defaultItem];
  }

  return transformation.config.dictionary;
};

const LookupDictionary = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr min-content;
  gap: ${s8};
`;

const From = styled.div`
  grid-column: 1;
`;

const To = styled.div`
  grid-column: 2;
`;

const AddNewButton = styled(SecondaryButton)`
  grid-column: 2;
`;

const createEmptyDictionaryItem = (toFieldType: FieldType): DictionaryItem => ({
  key: '',
  value: toFieldType === FieldType.TRANSFORM_TEXT ? '' : null,
});
